import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { t, locale } from '../i18n'

const pad = n => String(n).padStart(2, '0')
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const monthKey = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const STATUS_LABEL = { available: 'available', full: 'fully booked', closed: 'closed', past: 'unavailable' }

// Month calendar that colors every day by availability: green = has free
// times, red = fully booked, grey = closed or in the past. Only green days
// can be picked. `loadMonth(YYYY-MM)` must resolve to { days: { date: status } }.
export default function AvailabilityCalendar({ loadMonth, value, onChange, reloadKey }) {
  const [cursor, setCursor] = useState(() => {
    const d = value ? new Date(`${value}T12:00:00`) : new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })
  const [days, setDays] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const month = monthKey(cursor)
  const thisMonth = monthKey(new Date())

  useEffect(() => {
    let current = true
    setLoading(true)
    setError('')
    Promise.resolve(loadMonth(month))
      .then(result => {
        if (!current) return
        const loaded = result?.days || {}
        setDays(loaded)
        // First visit: jump straight to the first bookable day instead of
        // making people hunt for green.
        if (!value || !loaded[value]) {
          const first = Object.keys(loaded).sort().find(d => loaded[d] === 'available')
          if (first && (!value || value.slice(0, 7) === month)) onChange(first)
        }
      })
      .catch(() => { if (current) setError(t("Could not load availability. Try again.")) })
      .finally(() => { if (current) setLoading(false) })
    return () => { current = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, reloadKey])

  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
    const leading = (first.getDay() + 6) % 7 // Monday-first grid
    const count = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate()
    return [...Array(leading).fill(null), ...Array.from({ length: count }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i + 1))]
  }, [cursor])

  const today = ymd(new Date())
  const shift = delta => setCursor(c => new Date(c.getFullYear(), c.getMonth() + delta, 1))

  return (
    <div className="avail-cal">
      <div className="avail-cal-head">
        <button type="button" onClick={() => shift(-1)} disabled={month <= thisMonth} aria-label={t("Previous month")}><ChevronLeft size={18} /></button>
        <h3 aria-live="polite">{cursor.toLocaleDateString(locale(), { month: 'long', year: 'numeric' })}</h3>
        <button type="button" onClick={() => shift(1)} aria-label={t("Next month")}><ChevronRight size={18} /></button>
      </div>

      <div className="avail-cal-grid" role="grid" aria-busy={loading}>
        {WEEKDAYS.map(d => <span key={d} className="avail-cal-weekday" role="columnheader">{t(d)}</span>)}
        {cells.map((d, i) => {
          if (!d) return <span key={`blank-${i}`} />
          const key = ymd(d)
          const status = loading ? 'loading' : (days[key] || (key < today ? 'past' : 'closed'))
          const selectable = status === 'available'
          const selected = value === key
          return (
            <button
              key={key}
              type="button"
              role="gridcell"
              disabled={!selectable}
              aria-selected={selected}
              aria-label={`${d.toLocaleDateString(locale(), { weekday: 'long', day: 'numeric', month: 'long' })}, ${t(STATUS_LABEL[status] || 'loading')}`}
              title={t(STATUS_LABEL[status])}
              onClick={() => selectable && onChange(key)}
              className={`avail-day is-${status}${selected ? ' is-selected' : ''}${key === today ? ' is-today' : ''}`}
            >
              {d.getDate()}
            </button>
          )
        })}
      </div>

      {error && <p role="alert" className="text-sm text-red-700 mt-2">{error}</p>}

      <div className="avail-legend" aria-hidden="true">
        <span><i className="is-available" />{t("Available")}</span>
        <span><i className="is-full" />{t("Fully booked")}</span>
        <span><i className="is-closed" />{t("Closed")}</span>
      </div>
    </div>
  )
}
